import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { Save, RefreshCw } from 'lucide-react';
import { getSiteSettings, adminUpdateSiteSettings, getFeatured } from '@/lib/api';

/**
 * AdminHomepage
 *
 * Small settings panel for the parts of the homepage that were previously
 * hard-coded — currently the hero headline + hero subtitle. Leaving a field
 * blank clears the override and reverts to the featured-competition default
 * ("Win [title]" + subtitle from the featured comp).
 *
 * Italic emphasis: wrap a word or phrase in *asterisks* to render it in the
 * italic serif face — e.g. "Win a padel holiday in *Marbella*".
 */

// Same *italic* parser used by HomePage — kept small and dependency-free so the
// preview here is byte-identical to what the live homepage renders.
const renderInline = (raw) => {
  if (!raw) return null;
  const parts = String(raw).split('*');
  return parts.map((p, i) =>
    i % 2 === 1
      ? <em key={i} className="font-italic-serif">{p}</em>
      : <span key={i}>{p}</span>
  );
};

const AdminHomepage = () => {
  const [initial, setInitial] = useState({ hero_title_override: '', hero_subtitle_override: '', show_prize_categories: false, show_coming_soon_grid: false });
  const [title, setTitle] = useState('');
  const [subtitle, setSubtitle] = useState('');
  const [showCategories, setShowCategories] = useState(false);
  const [showComingSoon, setShowComingSoon] = useState(false);
  const [featured, setFeatured] = useState(null);
  const [saving, setSaving] = useState(false);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    Promise.all([
      getSiteSettings().catch(() => ({})),
      getFeatured().catch(() => null),
    ]).then(([settings, feat]) => {
      const t = settings?.hero_title_override || '';
      const s = settings?.hero_subtitle_override || '';
      const spc = !!settings?.show_prize_categories;
      const scs = !!settings?.show_coming_soon_grid;
      setInitial({ hero_title_override: t, hero_subtitle_override: s, show_prize_categories: spc, show_coming_soon_grid: scs });
      setTitle(t);
      setSubtitle(s);
      setShowCategories(spc);
      setShowComingSoon(scs);
      setFeatured(feat);
      setLoaded(true);
    });
  }, []);

  const derivedTitle = featured?.title ? `Win ${featured.title}` : 'Win a brand-new padel prize';
  const derivedSubtitle = featured?.subtitle || 'Enter now for your shot at the prize.';

  const previewTitle = title.trim() || derivedTitle;
  const previewSubtitle = subtitle.trim() || derivedSubtitle;

  const dirty = useMemo(
    () =>
      title !== initial.hero_title_override
      || subtitle !== initial.hero_subtitle_override
      || showCategories !== initial.show_prize_categories
      || showComingSoon !== initial.show_coming_soon_grid,
    [title, subtitle, showCategories, showComingSoon, initial]
  );

  const save = async () => {
    setSaving(true);
    try {
      const res = await adminUpdateSiteSettings({
        hero_title_override: title,
        hero_subtitle_override: subtitle,
        show_prize_categories: showCategories,
        show_coming_soon_grid: showComingSoon,
      });
      const t = res?.hero_title_override || '';
      const s = res?.hero_subtitle_override || '';
      const spc = !!res?.show_prize_categories;
      const scs = !!res?.show_coming_soon_grid;
      setInitial({ hero_title_override: t, hero_subtitle_override: s, show_prize_categories: spc, show_coming_soon_grid: scs });
      setTitle(t);
      setSubtitle(s);
      setShowCategories(spc);
      setShowComingSoon(scs);
      toast.success('Homepage settings updated — refresh the site to see the change.');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const revertToDefaults = () => {
    setTitle('');
    setSubtitle('');
    setShowCategories(false);
    setShowComingSoon(false);
  };

  return (
    <div data-testid="admin-homepage-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-display text-4xl">Homepage hero</h1>
          <p className="text-sm text-muted-sage mt-1 max-w-2xl">
            Edit the big headline and mini description that appear at the very top of the homepage.
            Leave a field blank to auto-generate it from whichever competition is currently featured.
            Wrap a word or phrase in <code className="font-mono text-[color:var(--pd-text)] px-1 bg-[color:var(--pd-bg)] rounded">*asterisks*</code> to render it in the italic serif face.
          </p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={revertToDefaults}
            disabled={!title && !subtitle}
            className="border border-pd rounded-xl px-3 py-2 text-sm text-muted-sage hover:text-[color:var(--pd-text)] disabled:opacity-40 inline-flex items-center gap-2"
            data-testid="admin-homepage-revert"
          >
            <RefreshCw size={14} /> Revert to defaults
          </button>
          <button
            onClick={save}
            disabled={!dirty || saving}
            className="bg-[color:var(--pd-accent-yellow)] text-[#14251c] rounded-xl px-4 py-2 font-medium disabled:opacity-50 inline-flex items-center gap-2"
            data-testid="admin-homepage-save"
          >
            <Save size={14} /> {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>

      {!loaded ? (
        <div className="mt-8 text-sm text-muted-sage">Loading…</div>
      ) : (
        <div className="mt-8 grid grid-cols-1 xl:grid-cols-[minmax(0,1fr)_minmax(0,600px)] gap-6">
          {/* Fields */}
          <div className="space-y-5">
            <label className="block">
              <div className="font-mono text-[10px] uppercase tracking-widest text-muted-sage mb-2">Hero headline</div>
              <textarea
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                rows={3}
                placeholder={`Default: ${derivedTitle}`}
                className="w-full rounded-xl bg-[color:var(--pd-bg)] border border-pd px-3 py-2 text-sm font-display text-lg focus:border-[color:var(--pd-accent-yellow)]"
                data-testid="admin-homepage-title-input"
              />
              <div className="mt-1 text-[11px] text-muted-sage">
                Example: <span className="font-mono">Win a padel holiday in *Marbella*</span> — the word in asterisks becomes italic.
              </div>
            </label>

            <label className="block">
              <div className="font-mono text-[10px] uppercase tracking-widest text-muted-sage mb-2">Hero subtitle</div>
              <textarea
                value={subtitle}
                onChange={(e) => setSubtitle(e.target.value)}
                rows={3}
                placeholder={`Default: ${derivedSubtitle}`}
                className="w-full rounded-xl bg-[color:var(--pd-bg)] border border-pd px-3 py-2 text-sm focus:border-[color:var(--pd-accent-yellow)]"
                data-testid="admin-homepage-subtitle-input"
              />
              <div className="mt-1 text-[11px] text-muted-sage">One or two short sentences. Same asterisk-italic syntax applies.</div>
            </label>

            <div className="rounded-xl border border-pd bg-[color:var(--pd-surface)] px-4 py-3 text-sm">
              <div className="font-mono text-[10px] uppercase tracking-widest text-muted-sage mb-2">Get Tickets button</div>
              <p className="text-muted-sage">
                The <span className="text-[color:var(--pd-text)]">Get Tickets</span> button on the hero automatically points to whichever competition is currently marked <strong className="text-[color:var(--pd-text)]">Featured</strong> in the Competitions list. You don&rsquo;t need to configure it here.
              </p>
            </div>
          </div>

          {/* Live preview */}
          <aside className="xl:sticky xl:top-4 xl:self-start space-y-4">
            {/* Visibility toggles for the two placeholder homepage sections.
                Both default off while only one competition is active and should
                stay off until there's enough stock to populate them properly. */}
            <div className="rounded-2xl border border-pd bg-[color:var(--pd-surface)]" data-testid="admin-homepage-toggles">
              <div className="px-4 py-2 border-b border-pd">
                <div className="font-mono text-[10px] uppercase tracking-widest text-muted-sage">Section visibility</div>
                <div className="text-xs text-muted-sage">Both default off while the site has one live competition</div>
              </div>
              <div className="p-4 space-y-3">
                <label className="flex items-start gap-3 cursor-pointer" data-testid="toggle-show-categories">
                  <input
                    type="checkbox"
                    checked={showCategories}
                    onChange={(e) => setShowCategories(e.target.checked)}
                    className="mt-1 accent-[color:var(--pd-accent-yellow)]"
                  />
                  <span className="text-sm">
                    <span className="text-[color:var(--pd-text)]">Show &ldquo;Prize categories&rdquo; slider</span>
                    <span className="block text-xs text-muted-sage mt-0.5">Padel Holidays, Premium Rackets, Club Memberships, etc. Hide while only one comp is live.</span>
                  </span>
                </label>
                <label className="flex items-start gap-3 cursor-pointer" data-testid="toggle-show-coming-soon">
                  <input
                    type="checkbox"
                    checked={showComingSoon}
                    onChange={(e) => setShowComingSoon(e.target.checked)}
                    className="mt-1 accent-[color:var(--pd-accent-yellow)]"
                  />
                  <span className="text-sm">
                    <span className="text-[color:var(--pd-text)]">Show &ldquo;Coming soon&rdquo; grid</span>
                    <span className="block text-xs text-muted-sage mt-0.5">Mystery-card previews of upcoming draws. Keep hidden until we have real upcoming comps queued.</span>
                  </span>
                </label>
              </div>
            </div>

            <div className="rounded-2xl border border-pd bg-[color:var(--pd-surface)] overflow-hidden" data-testid="admin-homepage-preview">
              <div className="px-4 py-2 border-b border-pd flex items-center justify-between">
                <div>
                  <div className="font-mono text-[10px] uppercase tracking-widest text-muted-sage">Live preview</div>
                  <div className="text-xs text-muted-sage">Exactly what the homepage will render</div>
                </div>
                {(!title.trim() || !subtitle.trim()) ? (
                  <div className="text-[10px] font-mono uppercase tracking-widest text-[color:var(--pd-gold)]">Using defaults</div>
                ) : null}
              </div>
              <div className="p-6 md:p-8 bg-[color:var(--pd-bg)]">
                <div className="font-mono text-[10px] tracking-[0.2em] uppercase text-[color:var(--pd-accent-yellow)]">Now drawing</div>
                <h2
                  className="mt-3 font-display leading-[0.95] tracking-[-0.01em] text-[color:var(--pd-text)]"
                  style={{ fontSize: 'clamp(2rem, 5.5vw, 3.75rem)' }}
                >
                  {renderInline(previewTitle)}
                </h2>
                <p className="mt-4 text-base text-muted-sage max-w-md">
                  {renderInline(previewSubtitle)}
                </p>
                <p className="mt-3 font-mono text-[10px] uppercase tracking-[0.24em] text-[color:var(--pd-gold)]">
                  Equal Odds, Equal Opportunity.
                </p>
                <div className="mt-5 flex flex-wrap gap-2">
                  <span className="inline-block bg-[color:var(--pd-accent-yellow)] text-[#14251c] px-4 py-2 rounded-xl text-sm font-medium">Get tickets</span>
                  <span className="inline-block border border-[color:var(--pd-accent-yellow)]/70 text-[color:var(--pd-accent-yellow)] px-4 py-2 rounded-xl text-sm">How it works</span>
                </div>
              </div>
              <div className="px-4 py-3 border-t border-pd text-[11px] text-muted-sage">
                Get Tickets currently links to: <span className="text-[color:var(--pd-text)]">{featured?.title || 'the featured competition'}</span>
              </div>
            </div>
          </aside>
        </div>
      )}
    </div>
  );
};

export default AdminHomepage;
